import React, { useEffect, useState, useCallback, useRef } from 'react';
import "../style.scss";
import {
    THeadRow,
    RowCells
} from '../uploadData/components/DueListTable.jsx'
import { DuelistFilter } from "../uploadData/components/DueListFilter";
import { DueListPagination } from "../uploadData/components/DueListPagination";
import { api } from "../../../services/api";
import { userDashboardService } from "../../../services/userDashboardService";
import LoaderBox from '../components/custom/loader/loaderBox';
import SkeletonTable from '../uploadData/components/SkeletonTable';
import ProcessModal from '../uploadData/components/ProcessModal';
import useAuth from 'hooks/useAuth';
import { useStaticData } from 'contexts/StaticDataContext';
import {
    Alert,
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Container,
    Input
} from 'reactstrap';
import axios from 'axios';
import { TableVirtuoso } from 'react-virtuoso';
import { Bar } from 'react-chartjs-2';

const colors = {
  gray: {
    100: "#f6f9fc",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#8898aa",
    700: "#525f7f",
    800: "#172430",
    900: "#212529",
  },
};

const StatusValuesChart = (props) => {
    // Apenas mostrar itens que realmente tem processo atribuído
    const activeData = props.data ? props.data.filter(s => s.quantity > 0) : [];
    
    // Ordernar do maior para o menor
    activeData.sort((a, b) => b.quantity - a.quantity);

    const chartData = {
        labels: activeData.map(s => s.name),
        total_quantity: activeData.reduce((acc, curr) => acc + curr.quantity, 0),
        datasets: [
            {
                data: activeData.map(s => s.quantity),
                label: "Quantidade: ",
                maxBarThickness: 45,
                backgroundColor: 'rgba(194, 100, 26, 0.85)', // Pax primary orange
                borderColor: 'rgba(194, 100, 26, 1)',
                borderWidth: 1,
                borderRadius: 4,
            }
        ]
    };

    return (
        <Card className="shadow-sm border-0 mb-4">
            <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="mb-0 text-uppercase text-muted ls-1 small font-weight-bold">Volume de Processos</h4>
                    <h3 className="mb-0 font-weight-700">Status da Atribuição</h3>
                </div>
            </CardHeader>
            <CardBody>
                <div style={{ position: 'relative', height: '350px', width: '100%' }}>
                    {activeData.length > 0 ? (
                        <Bar
                            data={chartData}
                            options={{
                                maintainAspectRatio: false,
                                responsive: true,
                                tooltips: {
                                    mode: 'index',
                                    intersect: false,
                                    callbacks: {
                                        label: function (item) {
                                            return " Quantidade: " + item.yLabel.toString().replace(/(\d)(?=(?:[0-9]{3})+\b)/g, "$1.");
                                        },
                                    },
                                },
                                scales: {
                                    xAxes: [{
                                        gridLines: { display: false },
                                        ticks: { autoSkip: false, fontColor: colors.gray[600] }
                                    }],
                                    yAxes: [{
                                        gridLines: {
                                            color: colors.gray[200],
                                            zeroLineColor: colors.gray[200],
                                            drawBorder: false
                                        },
                                        ticks: {
                                            beginAtZero: true,
                                            precision: 0,
                                            fontColor: colors.gray[600],
                                            callback: function (value) {
                                                if (value % 1 === 0) {
                                                    return value.toString().replace(/(\d)(?=(?:[0-9]{3})+\b)/g, "$1.");
                                                }
                                            },
                                        },
                                    }],
                                },
                            }}
                            className="chart-canvas"
                            id="chart-bars"
                        />
                    ) : (
                        <div className="d-flex w-100 h-100 flex-column justify-content-center align-items-center text-muted">
                            <i className="ni ni-chart-bar-32 display-4 opacity-5 mb-3" />
                            <p>Nenhum processo encontrado nos status ativos</p>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

const UserDashboard = () => {
    const { user } = useAuth();
    const { ordersStatus: logOrderStatus, users } = useStaticData();
    const [logMapData, setLogMapData] = useState({
        results: [],
        previous: null,
        next: null,
        count: 0,
        now: 0,
        nowEndpoint: null,
    });
    const [loader, setLoader] = useState(false);
    const [message, setMessage] = useState(null);
    const [progress, setProgress] = useState(undefined);
    const [exportCancellable, setExportCancellable] = useState(false);
    const [toast, setToast] = useState(null);
    const [openModalOrderId, setOpenModalOrderId] = useState(null);
    const [stats, setStats] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(
        user?.is_superuser ? null : localStorage.getItem('AUTHOR_ID')
    );

    const timerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const exportButtonRef = useRef(null);

    const showToast = useCallback((type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const getProcessData = useCallback(async (endpoint) => {
        try {
            const response = await api.get(endpoint);
            const { count, next: rawNext, previous: rawPrevious, results } = response.data;
            const next = rawNext?.replace(/^http:\/\//, 'https://') ?? null;
            const previous = rawPrevious?.replace(/^http:\/\//, 'https://') ?? null;
            const now = new URLSearchParams(endpoint.split("?")[1]).get('page') ? parseInt(new URLSearchParams(endpoint.split("?")[1]).get('page')) : 1;
            setLogMapData({ count, next, previous, results, now, nowEndpoint: endpoint });
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getStatsData = useCallback(async (endpoint) => {
        try {
            let user_id = null;
            if (endpoint.includes('user_associated_id=')) {
                try {
                    const searchParams = new URLSearchParams(endpoint.split('?')[1]);
                    user_id = searchParams.get('user_associated_id');
                } catch(e) {}
            }
            const req = user_id ? { user_id } : {};
            const response = await userDashboardService.fetchOverview(req);
            setStats(response.data);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const executeAPIFunctions = useCallback(async (endpoint = "/process/search/", options = { loader: true }) => {
        setLoader(options.loader);
        let targetEndpoint = endpoint;
        const userId = user?.is_superuser ? selectedUserId : localStorage.getItem('AUTHOR_ID');
        if (userId) {
             const separator = targetEndpoint.includes('?') ? '&' : '?';
             targetEndpoint += `${separator}user_associated_id=${userId}`;
        }
        await Promise.all([
            getStatsData(targetEndpoint),
            getProcessData(targetEndpoint),
        ]);
        setLoader(false);
    }, [getProcessData, getStatsData, selectedUserId, user]);

    useEffect(() => {
        executeAPIFunctions();
    }, [executeAPIFunctions]);

    const formatElapsed = (startTime) => {
        const secs = Math.floor((Date.now() - startTime) / 1000);
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}m ${s}s`;
    };

    const resetExportState = (button) => {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setLoader(false);
        setMessage(null);
        setProgress(undefined);
        setExportCancellable(false);
        if (button) button.disabled = false;
    };

    const cancelExport = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        resetExportState(exportButtonRef.current);
    }, []);

    const getExportedExcel = useCallback((e, filters_uri, filename) => {
        exportButtonRef.current = e.target;
        e.target.disabled = true;

        const startTime = Date.now();
        setMessage(`Gerando arquivo... 0m 0s`);
        setProgress(0);
        setLoader(true);
        setExportCancellable(true);

        timerRef.current = setInterval(() => {
            setMessage(`Gerando arquivo... ${formatElapsed(startTime)}`);
        }, 1000);

        abortControllerRef.current = new AbortController();

        api.get(`process/export?${filters_uri}`, {
            responseType: "blob",
            timeout: 600000,
            signal: abortControllerRef.current.signal,
            onDownloadProgress: (progressEvent) => {
                const elapsed = formatElapsed(startTime);
                if (progressEvent.total) {
                    const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setProgress(pct);
                    setMessage(`Baixando... ${pct}% — ${elapsed}`);
                }
            }
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            resetExportState(e.target);
            showToast('success', 'Excel gerado com sucesso.');
        }).catch((error) => {
            if (axios.isCancel(error)) return;
            console.error(error);
            resetExportState(e.target);
            showToast('error', 'Ocorreu um erro ao exportar o arquivo.');
        });
    }, [showToast]);

    return (
        <>
            {toast && (
                <Alert
                    role="alert"
                    color={toast.type === 'success' ? 'success' : toast.type === 'warning' ? 'warning' : 'danger'}
                    style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10000, minWidth: '280px' }}
                >
                    {toast.text}
                </Alert>
            )}

            {openModalOrderId && (() => {
                const openOrder = (logMapData.results || []).find((r) => r.id === openModalOrderId);
                return openOrder ? (
                    <ProcessModal
                        order={openOrder}
                        onClose={() => setOpenModalOrderId(null)}
                        reload={executeAPIFunctions}
                        endpoint={logMapData.nowEndpoint}
                        ordersStatus={logOrderStatus}
                        users={users}
                        showToast={showToast}
                    />
                ) : null;
            })()}

            <div className="content">
                <Container fluid>
                    <Row className="mb-4 align-items-center">
                        <Col>
                            <h2 className="mb-0 text-dark" style={{ fontWeight: "700" }}>
                                <i className="ni ni-chart-pie-35 text-primary mr-2" />
                                {user?.is_superuser
                                    ? (selectedUserId
                                        ? `Precatórios de ${users.find(u => String(u.id) === String(selectedUserId))?.first_name || ''} ${users.find(u => String(u.id) === String(selectedUserId))?.last_name || ''}`.trim()
                                        : 'Todos os Precatórios Atribuídos')
                                    : 'Meus Precatórios Atribuídos'}
                            </h2>
                            <p className="text-muted mb-0 small">
                                {user?.is_superuser && !selectedUserId
                                    ? 'Visão consolidada de todos os processos atribuídos.'
                                    : 'Visão consolidada da sua produtividade e processos sob sua responsabilidade.'}
                            </p>
                        </Col>
                        {user?.is_superuser && (
                            <Col xs="auto">
                                <Input
                                    type="select"
                                    data-testid="user-selector"
                                    value={selectedUserId || ''}
                                    onChange={(e) => setSelectedUserId(e.target.value || null)}
                                    style={{ minWidth: '200px' }}
                                >
                                    <option value="">Todos os usuários</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </option>
                                    ))}
                                </Input>
                            </Col>
                        )}
                    </Row>

                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0 mb-4">
                                <CardHeader className="bg-transparent pb-0">
                                    <h4 className="mb-0 text-muted font-weight-600">Configuração de Busca</h4>
                                </CardHeader>
                                <CardBody>
                                    <DuelistFilter showToast={showToast} reload={executeAPIFunctions} endpoint={logMapData.nowEndpoint} orderStatus={logOrderStatus} executeExport={getExportedExcel} recordCount={logMapData.count} />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {!loader && stats && (
                        <Row>
                            <Col>
                                <StatusValuesChart data={stats.process_status} />
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col>
                            <Card className="shadow-sm border-0">
                                <CardHeader className="bg-transparent pb-0 d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0 text-muted font-weight-600">
                                        Seus Processos 
                                        {logMapData.count > 0 && <span className="ml-2 badge badge-pill badge-primary">{logMapData.count}</span>}
                                    </h4>
                                </CardHeader>
                                <CardBody className="p-0">
                                    {loader ? (
                                        message ? (
                                            <div className="py-5">
                                                <LoaderBox
                                                    message={message}
                                                    progress={progress}
                                                    onCancel={exportCancellable ? cancelExport : undefined}
                                                />
                                            </div>
                                        ) : (
                                            <SkeletonTable />
                                        )
                                    ) : (
                                        <>
                                            <TableVirtuoso
                                                data={logMapData.results || []}
                                                style={{ height: 600 }}
                                                components={{
                                                    Table: (props) => <table id="custom-duelist-table" className="align-items-center table table-flush" {...props} />,
                                                    TableHead: React.forwardRef((props, ref) => <thead className="thead-light" ref={ref} {...props} />),
                                                    TableRow: (props) => <tr className="custom-duelist-row" {...props} />,
                                                    EmptyPlaceholder: () => (
                                                        <tbody>
                                                            <tr>
                                                                <td colSpan={9} className="text-center py-5">
                                                                    <i className="ni ni-folder-17 display-4 text-muted mb-3" />
                                                                    <p className="text-muted">Você não possui processos atribuídos para os filtros selecionados.</p>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    ),
                                                }}
                                                fixedHeaderContent={() => <THeadRow />}
                                                itemContent={(index, order) => (
                                                    <RowCells
                                                        showToast={showToast}
                                                        order={order}
                                                        i={index}
                                                        reload={executeAPIFunctions}
                                                        endpoint={logMapData.nowEndpoint}
                                                        ordersStatus={logOrderStatus}
                                                        users={users}
                                                        isModalOpen={false}
                                                        onOpenModal={() => setOpenModalOrderId(order.id)}
                                                        onCloseModal={() => setOpenModalOrderId(null)}
                                                    />
                                                )}
                                            />
                                            <div className="py-3 px-4 bg-transparent border-top">
                                                <DueListPagination data={[logMapData.count, logMapData.previous, logMapData.next, logMapData.now, logMapData.nowEndpoint]} reload={executeAPIFunctions} />
                                            </div>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    )
}

export default UserDashboard;
